import { useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const colorOptions = [
    { name: 'Slate', value: 'bg-slate-600' },
    { name: 'Red', value: 'bg-red-600' },
    { name: 'Orange', value: 'bg-orange-600' },
    { name: 'Amber', value: 'bg-amber-600' },
    { name: 'Yellow', value: 'bg-yellow-600' },
    { name: 'Lime', value: 'bg-lime-600' },
    { name: 'Green', value: 'bg-green-600' },
    { name: 'Emerald', value: 'bg-emerald-600' },
    { name: 'Teal', value: 'bg-teal-600' },
    { name: 'Cyan', value: 'bg-cyan-600' },
    { name: 'Blue', value: 'bg-blue-600' },
    { name: 'Indigo', value: 'bg-indigo-600' },
    { name: 'Violet', value: 'bg-violet-600' },
    { name: 'Purple', value: 'bg-purple-600' },
    { name: 'Fuchsia', value: 'bg-fuchsia-600' },
    { name: 'Pink', value: 'bg-pink-600' },
];

const timezones = [
    { label: "Pacific Time (PT)", value: "America/Los_Angeles" },
    { label: "Mountain Time (MT)", value: "America/Denver" },
    { label: "Central Time (CT)", value: "America/Chicago" },
    { label: "Eastern Time (ET)", value: "America/New_York" },
    { label: "Atlantic Time (AT)", value: "America/Halifax" },
    { label: "British Time (BST/GMT)", value: "Europe/London" },
    { label: "Central European Time (CET)", value: "Europe/Paris" },
    { label: "Eastern European Time (EET)", value: "Europe/Helsinki" },
    { label: "India (IST)", value: "Asia/Kolkata" },
    { label: "Singapore/Hong Kong", value: "Asia/Singapore" },
    { label: "Japan (JST)", value: "Asia/Tokyo" },
    { label: "Australia Eastern (AEST)", value: "Australia/Sydney" },
    { label: "New Zealand (NZST)", value: "Pacific/Auckland" },
    { label: "UTC (Universal Time)", value: "UTC" },
];

const UserInfoStep = ({ stepKey, updateStepData, getStepData }) => {
    const stepData = getStepData(stepKey);

    useEffect(() => {
        if (stepData) {
            // Set default color if not already set
            if (!stepData.avatarColor) {
                updateStepData(stepKey, { ...stepData, avatarColor: 'bg-neutral-600' });
            } else {
                updateStepData(stepKey, stepData);
            }
        }
    }, []);

    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        const first = nameParts[0][0] || '';
        const last = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : '';
        return (first + last).toUpperCase();
    };

    return (
        <div className="flex flex-col space-y-8 items-center">
            <div className="flex flex-col items-center space-y-3">
                <h1 className="font-semibold text-3xl text-center">Set your personal details</h1>
                <p className="text-center text-muted-foreground">Personalize your profile and make it easy for others to find you</p>
            </div>
            <div className="w-full max-w-lg mx-auto space-y-8">
                <div className="space-y-3">
                    <Label htmlFor="name">Full name</Label>
                    <Input
                        id="name"
                        value={stepData.name}
                        onChange={(e) => updateStepData(stepKey, { ...stepData, name: e.target.value })}
                        placeholder="Enter your full name"
                        className="h-auto"
                    />
                </div>

                <Separator />

                <div className="space-y-4">
                    <Label>Avatar color</Label>
                    <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                            <AvatarFallback className={`text-xl ${stepData.avatarColor} text-neutral-200`}>
                                {getInitials(stepData.name) || 'SB'}
                            </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-muted-foreground">
                            Choose a color for your avatar
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {colorOptions.map((color) => (
                            <button
                                key={color.value}
                                className={`w-7 h-7 rounded-full ${color.value} transition-all hover:scale-110 p-1 ${
                                    stepData.avatarColor === color.value ? 'ring-2 ring-offset-2 ring-offset-background ring-ring' : ''
                                }`}
                                onClick={() => updateStepData(stepKey, { ...stepData, avatarColor: color.value })}
                                title={color.name}
                            />
                        ))}
                    </div>
                </div>

                <Separator />

                <div className="space-y-3">
                    <Label>Timezone</Label>
                    <Select
                        value={stepData.timezone}
                        onValueChange={(value) => updateStepData(stepKey, { ...stepData, timezone: value })}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select your timezone" />
                        </SelectTrigger>
                        <SelectContent>
                            {timezones.map((tz) => (
                                <SelectItem key={tz.value} value={tz.value}>
                                    {tz.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">
                        Choose your timezone for accurate date and time display
                    </span>
                </div>
            </div>
        </div>
    );
};

export default UserInfoStep; 